import { Subscription } from 'rxjs';
import { Component, OnInit, OnDestroy } from '@angular/core';

import {
  EntityValidationErrorResponse,
  SelectableTransaction, Transaction,
  Entity, GenerateReportPayload,
  FinancialDataValueEnteredInEnum,
  Container
} from '../../../shared/models';
import { PaginationConstants, TinyTextEditor } from '../../../shared/constants';

import { ModalService } from '../../../shared/modules/modal/modal.service';
import {
  NotificationService,
  UtilsService,
  EntitiesService,
  TpReportGeneratorService,
  SessionService,
  CompSearchService
} from '../../../shared/services';

import {
  ClosableModalWithErrorResponseModel
} from '../../../shared/components/base/closable-modal-with-error-response-model';

import { TranslationConstants } from '../../../shared/constants/translation';
import { LocalFileGenerateReportModalPayload } from '../../../shared/models/modal-payload/local-file-generate-report-modal-payload';

@Component({
  selector: 'generate-report-modal',
  templateUrl: './generate-report-modal.component.html',
})
export class GenerateReportModalComponent
  extends ClosableModalWithErrorResponseModel<EntityValidationErrorResponse>
  implements OnInit, OnDestroy {

  data: LocalFileGenerateReportModalPayload;
  fileToUpload: File;
  subscriptions: Subscription[] = [];
  reportTitle: string;
  tradingPartnerId: number;
  potentialTradingPartners: Entity[] = [];
  transactions: SelectableTransaction[] = [];
  viewedTransactions: SelectableTransaction[] = [];
  selectedStudyId: number = null;
  currencyOptionId: number;
  templateId: string;
  language: string;
  languages = [];

  inputCurrencyOptions = [];
  pageSizes: number[];
  workingContainer: Container;

  currentPage = 1;
  totalItems: number;
  selectedPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;

  generatingReport: boolean = false;
  generatedReport: boolean = false;

  executiveSummaryIntro: string | null = null;
  executiveSummaryConclusion: string | null = null;

  tinyInit = TinyTextEditor.INIT;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _utilsService: UtilsService,
    private _entitiesService: EntitiesService,
    private _sessionService: SessionService,
    private _tpReportGeneratorService: TpReportGeneratorService,
    private _compSearchService: CompSearchService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new EntityValidationErrorResponse();
    this.workingContainer = this._sessionService.getWorkingContainer();
  }

  ngOnInit(): void {
    this.listenForErrorMessages();
    this.pageSizes = PaginationConstants.PAGE_SIZES;
    this.potentialTradingPartners = this.data.potentialTradingPartners;
    this.getCurrencyOptions();
    this.language = 'en'; // default
    this.getLanguages(this.data.entity.taxJurisdiction.countryId, this.workingContainer.taxYear);
    // this is a hack, couldn't get the dropdown to populate without it
    setTimeout(() => { this.templateId = this.data.templates[0].templateId; });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => { subscription.unsubscribe(); });
  }

  exitModal(): void {
    this.closeAllModals();
  }

  getCurrencyOptions(): void {
    if (this.data && this.data.entity &&
      this.data.entity.legalEntityCurrencyConversion &&
      this.data.entity.legalEntityCurrencyConversion.length) {
      this.inputCurrencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
    } else {
      this.inputCurrencyOptions = [{
        id: 1,
        name: FinancialDataValueEnteredInEnum[1],
      }];
    }
  }
  generateInternalLocalfileReport(): void {
    const selectedTransactions = this.transactions.filter(transaction => transaction.selected);
    const selectedTransactionIds = selectedTransactions.map(transaction => transaction.transactionId);
    let currencyId;

    if (this.currencyOptionId === 1) {
      currencyId = this.data.mneCurrency.currencyId;
    } else if (this.currencyOptionId === 2) {
      currencyId = this.data.taxJurisdictionCurrency.currencyId;
    }

    const payload: GenerateReportPayload = {
      currencyId,
      selectedTransactionIds,
      name: this.reportTitle,
      templateId: this.templateId,
      containerId: this.workingContainer.containerId,
      primaryEntityId: this.data.entity.entityId,
      tradingPartnerId: this.tradingPartnerId,
      mneCurrency: this.currencyOptionId === 1,
      mneId: this.data.mneId,
      executiveSummaryIntro: this.executiveSummaryIntro,
      executiveSummaryConclusion: this.executiveSummaryConclusion,
      targetLanguage: this.language,
      taxYear: this.workingContainer.taxYear
    };

    this.responseErrors = [];
    const errorMessages = this.validateReportPayload(payload);

    if (errorMessages.length) {
      this.responseErrors = errorMessages;
      return;
    }

    this.generatingReport = true;
    this._tpReportGeneratorService.generateReport(payload).subscribe(() => {
      this.generatingReport = false;
      this.generatedReport = true;

      // success is here becuse we need the exitModal function to close for both success and failures
      this.success();
      this.exitModal();
    });
  }

  getCurrencyIsoCode(optionId: number): string {
    let currency = this.data && this.data.taxJurisdictionCurrency && this.data.taxJurisdictionCurrency.isoCode;
    if (optionId === FinancialDataValueEnteredInEnum.MNE) {
      currency = this.data && this.data.mneCurrency && this.data.mneCurrency.isoCode;
    }

    return `${currency}`;
  }

  validateReportPayload(payload: any): string[] {
    const errorMessages = [];

    if (!payload.name) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_REPORT_TITLE_REQUIRED);
    } else if (payload.name.includes(`"`)) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_REPORT_DOUBLE_QUOTES);
    }

    if (!payload.tradingPartnerId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_TRADING_PARTNER_SELECTED);
    }

    if (!payload.currencyId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_CURRENCY_SELECTED);
    }

    if (!payload.templateId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_TEMPLATE_MUST_BE_SELECTED);
    }

    if (payload.selectedTransactionIds.length === 0) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_TRANSACTIONS_SELECTED);
    }

    if (payload.executiveSummaryIntro === '' || payload.executiveSummaryIntro === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_EXECUTIVE_SUMMARY_INTRO_MISSING);
    }

    if (payload.executiveSummaryConclusion === '' || payload.executiveSummaryConclusion === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_EXECUTIVE_SUMMARY_CONCLUSION_MISSING);
    }

    if (payload.targetLanguage === '' || payload.targetLanguage === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_LANGUAGE_MUST_BE_SELECTED);
    }

    return errorMessages;
  }

  tradingPartnerChange(tradingPartnerId: number): void {
    this.tradingPartnerId = tradingPartnerId;
    this.selectedStudyId = null;
    this.getNewTransactions();
  }

  getLanguages(jurisdictionId: number, taxYear: number): void {
    this._compSearchService.getLanguages(jurisdictionId, taxYear).subscribe((res) => {
      this.languages = res.data.sort((language1, language2) =>
        language1.language_iso > language2.language_iso ? 1 : -1)
        .map((language) => language.language_iso);
    });
  }

  getNewTransactions(transactionToKeep?: SelectableTransaction) {
    this.currentPage = 1;
    this._entitiesService.getEntityPairTransactions(
      this.data.entity.entityId, this.tradingPartnerId,
      `?${this.getPaginationQueryString()}&${this.getStudyQueryString()}`
    ).subscribe((res) => {
      let transactionToKeepIsInNewTransactions = false;

      this.transactions = res.data.map((transaction: any) => {
        if (transactionToKeep && transactionToKeep.transactionId === transaction.transactionId) {
          transaction.selected = true;
          transactionToKeepIsInNewTransactions = true;
        } else {
          transaction.selected = false;
        }
        return transaction;
      });

      this.viewedTransactions = this.transactions;

      if (transactionToKeep && !transactionToKeepIsInNewTransactions) {
        this.transactions.push(transactionToKeep);
      }

      this.totalItems = res.count;
    });
  }

  getNextTransactions(): void {
    this._entitiesService.getEntityPairTransactions(
      this.data.entity.entityId,
      this.tradingPartnerId,
      `?${this.getPaginationQueryString()}&${this.getStudyQueryString()}`
    ).subscribe((res) => {
      this.viewedTransactions = res.data.map((transaction: any) => {
        const foundTransaction = this.transactions.find(trs => trs.transactionId === transaction.transactionId);

        transaction.selected = foundTransaction ? foundTransaction.selected : false;

        return transaction;
      });
      this.addNewTransactions(this.viewedTransactions);
      this.totalItems = res.count;
    });
  }

  addNewTransactions(newTransactions: SelectableTransaction[]): void {
    newTransactions.forEach((newTransaction) => {
      if (!this.transactions.find(transaction => transaction.transactionId === newTransaction.transactionId)) {
        this.transactions.push(newTransaction);
      }
    });
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getNextTransactions();
  }

  pageSizeChanged(): void {
    this.getNextTransactions();
  }

  selectedTransactionChange(transactionId: number): void {
    const selectedTransaction = this.transactions.find(transaction => transaction.transactionId === transactionId);

    selectedTransaction.selected = !selectedTransaction.selected;

    if (selectedTransaction.selected) {
      if (this.selectedStudyId !== selectedTransaction.study.studyId) {
        this.selectedStudyId = selectedTransaction.study.studyId;
        this.getNewTransactions(selectedTransaction);
      }
    } else {
      const noSelectedTransactions = this.transactions.every(transaction => !transaction.selected);
      if (noSelectedTransactions) {
        this.selectedStudyId = null;
        this.getNewTransactions();
      }
    }
  }

  getEntityCode(transaction: Transaction, isSource = true): string {
    return this._utilsService.getEntityCodeFromTransaction(transaction, isSource);
  }

  getPaginationQueryString(): string {
    const queryParameters = [];
    let queryString: string;
    if (!!this.selectedPageSize) {
      queryParameters.push(`length=${this.selectedPageSize}`);
    }
    if (!!this.currentPage) {
      queryParameters.push(`page=${this.currentPage}`);
    }
    if (queryParameters.length > 0) {
      queryString = `${queryParameters.join('&')}`;
    }
    return queryString;
  }

  getStudyQueryString(): string {
    let queryString = '';

    if (this.selectedStudyId) {
      queryString = `study=${this.selectedStudyId}`;
    }

    return queryString;
  }

}
